import api from './api';

export interface Subcontractor {
  id: string;
  contract_number: string;
  name: string;
  email?: string;
  phone?: string;
  contract_subject: string;
  progress: number; // Będzie obliczane przez backend
  contract_value: number;
  invoiced_amount: number; // Będzie obliczane przez backend
  pdz: number; // Pozostało do zafakturowania (w PLN) - będzie obliczane przez backend
  completion_date: string; // Termin wykonania umowy (TWU)
  signed_date?: string; // Data podpisania umowy
  days_remaining: number; // Pozostało dni (PD) - będzie obliczane przez backend
  project_id: string;
  drive_file_id?: string;
  drive_file_name?: string;
  drive_folder_id?: string;
}

// Interfejs dla danych przesyłanych przy tworzeniu podwykonawcy
export interface CreateSubcontractorData {
  contract_number: string;
  name: string;
  email?: string;
  phone?: string;
  contract_subject: string;
  contract_value: number;
  completion_date: string;
  signed_date?: string;
  project_id: string;
  contract_draft_id?: string;
}

// Interfejs rozszerzający dla danych z plikiem
export interface CreateSubcontractorDataWithFile extends CreateSubcontractorData {
  contract_file?: File;
}

// Interfejs dla aktualizacji podwykonawcy z plikiem
export interface UpdateSubcontractorDataWithFile extends Partial<Subcontractor> {
  contract_file?: File;
}

export interface SubcontractorResponse {
  subcontractors: Subcontractor[];
  pagination: {
    total: number;
    totalPages: number;
    currentPage: number;
    limit: number;
  };
}

const subcontractorService = {
  getSubcontractorsByProjectId: async (projectId: string): Promise<Subcontractor[]> => {
    try {
      const response = await api.get(`/subcontractors/project/${projectId}`);
      return response.data;
    } catch (error) {
      console.error('Błąd podczas pobierania podwykonawców:', error);
      throw error;
    }
  },

  getSubcontractorById: async (id: string): Promise<Subcontractor> => {
    try {
      const response = await api.get(`/subcontractors/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Błąd podczas pobierania podwykonawcy o ID ${id}:`, error);
      throw error;
    }
  },

  createSubcontractor: async (subcontractorData: CreateSubcontractorData | CreateSubcontractorDataWithFile): Promise<Subcontractor> => {
    try {
      // Tworzenie FormData, jeśli przesyłamy plik
      if ('contract_file' in subcontractorData) {
        const formData = new FormData();
        
        // Dodanie pliku do FormData
        const fileData = subcontractorData as CreateSubcontractorDataWithFile;
        if (fileData.contract_file) {
          formData.append('contract_file', fileData.contract_file);
        }
        
        // Dodanie pozostałych danych do FormData
        (Object.keys(subcontractorData) as Array<keyof typeof subcontractorData>).forEach(key => {
          if (key !== 'contract_file' && subcontractorData[key] !== undefined) {
            // Konwersja wartości na string, ponieważ FormData przyjmuje tylko string lub Blob
            formData.append(key, String(subcontractorData[key]));
          }
        });
        
        const response = await api.post('/subcontractors', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        return response.data;
      } else {
        // Jeśli nie przesyłamy pliku, wysyłamy dane jako JSON
        const response = await api.post('/subcontractors', subcontractorData);
        return response.data;
      }
    } catch (error) {
      console.error('Błąd podczas tworzenia podwykonawcy:', error);
      throw error;
    }
  },

  updateSubcontractor: async (id: string, subcontractorData: Partial<Subcontractor> | UpdateSubcontractorDataWithFile): Promise<Subcontractor> => {
    try {
      // Sprawdzenie, czy przesyłamy plik
      if ('contract_file' in subcontractorData) {
        const formData = new FormData();
        
        // Dodanie pliku do FormData
        const fileData = subcontractorData as UpdateSubcontractorDataWithFile;
        if (fileData.contract_file) {
          formData.append('contract_file', fileData.contract_file);
        }
        
        // Dodanie pozostałych danych do FormData
        (Object.keys(subcontractorData) as Array<keyof typeof subcontractorData>).forEach(key => {
          if (key !== 'contract_file' && subcontractorData[key] !== undefined) {
            // Konwersja wartości na string, ponieważ FormData przyjmuje tylko string lub Blob
            formData.append(key, String(subcontractorData[key]));
          }
        });
        
        const response = await api.put(`/subcontractors/${id}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        return response.data;
      } else {
        // Jeśli nie przesyłamy pliku, wysyłamy dane jako JSON
        const response = await api.put(`/subcontractors/${id}`, subcontractorData);
        return response.data;
      }
    } catch (error) {
      console.error(`Błąd podczas aktualizacji podwykonawcy o ID ${id}:`, error);
      throw error;
    }
  },

  deleteSubcontractor: async (id: string): Promise<void> => {
    try {
      await api.delete(`/subcontractors/${id}`);
    } catch (error) {
      console.error(`Błąd podczas usuwania podwykonawcy o ID ${id}:`, error);
      throw error;
    }
  }
};

export default subcontractorService;
